<template>
  <div>

    <div class="box">
      <div class="w-full md:w-1/2 xl:w-1/4" v-if="mode === 'create'">
        <label class="block font-bold mb-2" for="list-name">List Name:</label>
        <input
          name="list_name"
          type="text"
          id="list-name"
          class="form-control"
          v-model="listName"
        >
        <button class="btn btn-green py-2 px-4 mt-4" @click="save">
          <i class="fas fa-check"></i>
          <span class="ml-3">Create</span>
        </button>
      </div>

      <div v-else>

        <div class="flex flex-wrap items-end justify-between">
          <div class="mb-1">
            <label for="list-name" class="mb-0">List name:</label>
            <div class="flex mt-4 items-center">
              <input
                name="list-name"
                type="text"
                id="list-name"
                class="form-control form-no-outline h-10 rounded-none rounded-l"
                :class="{ 'rounded-r': list.name === initial.name }"
                autocomplete="off"
                v-model="list.name"
              >
              <button
                v-if="list.name !== initial.name"
                type="button"
                class="h-10 px-5 border border-l-0 border-purple-400 light:border-purple-600 rounded-r focus:outline-none hover:bg-purple-800 hover:border-purple-700"
                @click="save"
              >Save</button>
            </div>
          </div>

          <div class="flex">
            <div
              v-if="hasPermission('reminder-list.edit') && showActions"
              class="btn text-sm px-4 flex items-center mb-1"
              :class="!editor.recipient.active ? 'btn-purple' : 'bg-theme-300 light:bg-theme-600'"
              :disabled="editor.recipient.active"
              @click="editor.recipient.active = true"
            >
              <i class="fas fa-plus"></i>
              <span class="ml-2">Add recipient</span>
            </div>

            <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="get">
              <i class="fas fa-sync-alt"></i>
            </button>
          </div>

        </div>

        <p class="text-lg text-center my-12" v-if="!hasRecipients && !editor.recipient.active">No data to display</p>
        <div class="relative" ref="tableWrap" v-else>
          <div class="w-full overflow-x-auto" @scroll="resizeProductModelSelect">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Method</th>
                  <th>Email</th>
                  <th>Phone number</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(recipient, i) in list.recipients" :key="i">
                  <template v-if="editingRecipientIndex === null || i < editingRecipientIndex">
                    <td>
                      <a class="text-link" @click.stop="$router.push('/customer/details/' + recipient.contact.id)">
                        #{{ recipient.contact.id }} {{ recipient.contact.first_name }} {{ recipient.contact.last_name }}
                        <template v-if="recipient.contact.company_name">
                          ({{ recipient.contact.company_name }})
                        </template>
                      </a>
                    </td>
                    <td>{{ recipient.method }}</td>
                    <td>{{ recipient.contact.email }}</td>
                    <td>
                      +{{ recipient.phone_number.countryData.calling_code }} {{ recipient.phone_number.number }}
                    </td>
                    <td class="text-center" v-if="showActions">
                      <div class="flex">
                        <button type="button" class="btn btn-blue text-xs py-2 px-3" @click="editRecipient(i)">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button type="button" class="btn btn-red text-xs py-2 px-3 ml-2" @click="removeContact(i)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>

              <ValidationObserver
                ref="editorObserver"
                v-slot="{ invalid }"
                tag="tbody"
                v-show="showActions"
              >
                <tr v-if="editor.recipient.active">
                  <td ref="editorModelSelectCell">

                    <div class="absolute mx-auto flex flex-wrap justify-end" ref="editorModelSelectWrap">
                      <select class="form-control mb-1" id="account-type" v-model="editor.recipient.model" @change="editor.recipient.contact = {}">
                        <option :value="model" v-for="(model, i) in editor.recipient.models" :key="'account-types-' + i">{{ model }}</option>
                      </select>

                      <model-select
                        class="form-control border-0 p-0"
                        ref="editorModelSelect"
                        :model="editor.recipient.model"
                        :form-name="editor.recipient.model"
                        v-model="editor.recipient.contact"
                        :list.sync="contactList"
                        :exclude-list="recipientContactIDs"
                        :search-on-mount="true"
                        :is-taggable="true"
                        :button-new="false"
                        :show-title="false"
                        :no-wrap="true"
                        @select="selectedContact"
                      ></model-select>

                      <button
                        v-if="editor.recipient.contact && editor.recipient.contact.id"
                        type="button"
                        class="btn text-xs btn-orange py-1 flex items-center"
                        @click="editContact"
                      >
                        <i class="fas fa-edit"></i>
                        <span class="ml-2">Edit</span>
                      </button>
                    </div>

                  </td>
                  <td>
                    <ValidationProvider
                      rules="required"
                      name="method"
                      v-slot="{ errors }"
                    >
                      <select name="method" class="form-control" id="method" v-model="editor.recipient.data.method">
                        <option :value="method" v-for="(method, i) in config.reminderMethods" :key="'methods-' + i">{{ method }}</option>
                      </select>
                      <span class="text-sm text-red-500">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                  <td>
                    <input type="text" disabled class="form-control p-1" v-model="editor.recipient.contact.email">
                  </td>
                  <td>
                    <ValidationProvider
                      rules="required"
                      name="phone number"
                      v-slot="{ errors }"
                    >
                      <select name="phone_number" class="form-control" id="phone-number" v-model="editor.recipient.data.contact_phone_number_id">
                        <option :value="phone.id" v-for="(phone, i) in editor.recipient.contact.phone_numbers" :key="'phone-numbers-' + i">+{{ phone.countryData.calling_code }} {{ phone.number }}</option>
                      </select>
                      <span class="text-sm text-red-500">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </td>
                  <td>
                    <div class="flex">
                      <button type="button" class="btn btn-green text-xs py-2 px-3" @click="saveRecipient">
                        <i class="fas fa-check"></i>
                      </button>
                      <button type="button" class="btn btn-red text-xs py-2 px-3 ml-2" @click="closeRecipientEditor">
                        <i :class="'fas ' + (editingRecipientIndex !== null ? 'fa-undo' : 'fa-times')"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </ValidationObserver>

              <tbody>
                <tr v-for="(recipient, i) in list.recipients" :key="i">
                  <template v-if="editingRecipientIndex !== null && i > editingRecipientIndex">
                    <td>
                      <a class="text-link" @click.stop="$router.push('/customer/details/' + recipient.contact.id)">
                        #{{ recipient.contact.id }} {{ recipient.contact.first_name }} {{ recipient.contact.last_name }}
                        <template v-if="recipient.contact.company_name">
                          ({{ recipient.contact.company_name }})
                        </template>
                      </a>
                    </td>
                    <td>{{ recipient.method }}</td>
                    <td>{{ recipient.contact.email }}</td>
                    <td>
                      +{{ recipient.phone_number.countryData.calling_code }} {{ recipient.phone_number.number }}
                    </td>
                    <td class="text-center" v-if="showActions">
                      <div class="flex">
                        <button type="button" class="btn btn-blue text-xs py-2 px-3" @click="editRecipient(i)">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button type="button" class="btn btn-red text-xs py-2 px-3 ml-2" @click="removeContact(i)">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>

          </div>
        </div>

        <div class="box p-0 pt-4 flex flex-wrap justify-end items-center" v-if="inModal">
          <button
            type="button"
            class="btn btn-reset"
            @click="reset"
          >
            <i class="fa-xs fas fa-undo"></i>
            <span class="ml-1">RESET</span>
          </button>
          <button
            type="button"
            class="btn btn-save"
            @click="save(true)"
          >
            <i class="fa-xs fas fa-check"></i>
            <span class="ml-2">{{ mode === 'edit' ? 'UPDATE' : 'SAVE' }}</span>
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  props: {
    listID: {},
    createAlert: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    inModal: Boolean,
  },
  data() {
    return {
      mode: null,
      editID: null,
      listName: '', // for create mode
      searchInput: '',
      query: {
        search: '',
      },
      initial: {
        name: '',
      },
      list: {},
      contactList: [], // for model-select only
      editingRecipientIndex: null,
      editor: {
        recipient: {
          active: false,
          model: 'customer',
          models: ['customer', 'supplier'],
          contact: {},
          data: {
            contact_id: '',
            contact_phone_number_id: '',
            method: '',
          },
        },
      },
      delays: {
        contact: {
          d: null,
          t: 500,
        },
        positionProductSelect: {
          d: null,
          t: 150,
        },
      }
    }
  },
  watch: {
    'editor.recipient.active'(v) {
      if (v) {
        this.$nextTick(() => setTimeout(this.resizeProductModelSelect, 10))
      }
    },
    listID() {
      this.setMode()
    },
  },
  computed: {
    recipientContactIDs() {
      if (!this.list.recipients || !this.list.recipients.length) {
        return []
      }
      return this.list.recipients.map(e => e.contact.id)
    },
    hasRecipients() {
      if (!this.list.recipients) {
        return false
      }
      return Object.entries(this.list.recipients).length > 0
    },
    ...mapGetters({
      CountryByID: 'countries/countryByID',
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  mounted() {
    this.resizeProductModelSelect()
    window.addEventListener('resize', this.resizeProductModelSelect)

    this.setMode()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeProductModelSelect)
  },
  methods: {
    resizeProductModelSelect() {
      if (!this.editor.recipient.active) {
        return
      }

      let delay = this.delays.positionProductSelect
      clearTimeout(delay.d)

      delay.d = setTimeout(() => {
        let compRect = this.$refs.tableWrap.getBoundingClientRect()
        let rect = this.$refs.editorModelSelectCell.getBoundingClientRect()
        let wrapRect = this.$refs.editorModelSelectWrap.getBoundingClientRect()

        if (rect.height < wrapRect.height) {
          this.$refs.editorModelSelectCell.style.height = (wrapRect.height + 10) + 'px'
          rect = this.$refs.editorModelSelectCell.getBoundingClientRect()
          wrapRect = this.$refs.editorModelSelectWrap.getBoundingClientRect()
        }
        this.$refs.editorModelSelectWrap.style.width = (rect.width - 20)  + 'px'
        this.$refs.editorModelSelectWrap.style.top = (rect.top - compRect.top) + ((rect.height - wrapRect.height) / 2)  + 'px'
        this.$refs.editorModelSelectWrap.style.left = (rect.left - compRect.left)  + 'px'
        delay.d = null
      }, delay.d ? delay.t : 0)
    },
    editRecipient(index) {
      this.editingRecipientIndex = index
      this.editor.recipient.data.contact_id = this.list.recipients[this.editingRecipientIndex].contact_id
      this.editor.recipient.data.contact_phone_number_id = this.list.recipients[this.editingRecipientIndex].contact_phone_number_id
      this.editor.recipient.data.method = this.list.recipients[this.editingRecipientIndex].method

      this.selectedContact({id: this.editor.recipient.data.contact_id})
        .then(() => {
          this.contactList = []
          this.contactList.push(this.editor.recipient.contact)

          this.editor.recipient.active = true
        })
    },
    editContact() {
      this.$refs.editorModelSelect.edit()
    },
    removeContact(index) {
      this.$swal.fire({
        title: 'Delete recipient',
        text: 'Are you sure?',
        type: 'question',
        showCancelButton: true,
      })
        .then((result) => {
          if (result.value) {
            this.closeRecipientEditor()
            this.contactList = []
            this.list.recipients.splice(index, 1)
            this.save()
          }
        })
    },
    selectedContact(contact) {
      return new Promise ((resolve, reject) => {
        axios.get(this.editor.recipient.model + 's/' + contact.id)
          .then(response => {
            let contact = response.data

            if (contact.phone_numbers && contact.phone_numbers.length) {
              contact.phone_numbers.sort((a) => {
                return a.type === 'primary' ? -1 : 1
              }).map(p => p.countryData = this.CountryByID(p.country_id))

              this.editor.recipient.data.contact_phone_number_id = contact.phone_numbers[0].id
            }

            contact.display_name =  '#' + contact.id + ' ' + contact.first_name + ' ' + contact.last_name + (contact.company_name ? ' (' + contact.company_name + ')' : '')

            this.editor.recipient.contact = contact
            this.resizeProductModelSelect()
            resolve(contact)
          })
          .catch(error => reject(error))
      })
    },
    async saveRecipient () {
      const isValid = await this.$refs.editorObserver.validate()
      if (!isValid) {
        this.$notify({type: 'error', title: 'Form has errors'})
        return
      }
      this._saveRecipient()
    },
    _saveRecipient() {
      if (!this.editor.recipient.contact || !Object.entries(this.editor.recipient.contact).length) {
        this.$notify({
          type: 'error',
          title: 'Please select contact',
          text: 'Contact cannot be empty'
        })
        return
      }
      this.editor.recipient.data.contact_id = this.editor.recipient.contact.id

      // the following exist only for template errors
      // not needed since we are getting list again after save
      this.editor.recipient.data.contact = this.editor.recipient.contact
      this.editor.recipient.data.phone_number = this.editor.recipient.data.contact.phone_numbers.filter((a) => {
        return a.id === this.editor.recipient.data.contact_phone_number_id
      })[0]
      this.editor.recipient.data.phone_number.countryData = this.CountryByID(this.editor.recipient.data.phone_number.country_id)

      if (this.editingRecipientIndex === null) {
        if (typeof this.list.recipients === 'undefined') {
          this.list.recipients = []
        }
        // create
        this.list.recipients.push(Object.assign({}, this.editor.recipient.data))
      } else {
        // edit
        this.$set(this.list.recipients, this.editingRecipientIndex, Object.assign(this.list.recipients[this.editingRecipientIndex], this.editor.recipient.data))
      }

      this.save()
        .then(() => {
          this.closeRecipientEditor()
          this.get()
        })
        .catch(() => {
          // revert
          if (this.editingRecipientIndex === null) {
            this.list.recipients = Object.assign([])
          } else {
            this.list.recipients.splice(this.editingRecipientIndex, 1)
          }
        })
    },
    closeRecipientEditor() {
      this.contactList = []
      this.editingRecipientIndex = null
      this.editor.recipient.contact = {}
      this.editor.recipient.data.contact_id = ''
      this.editor.recipient.data.contact_phone_number_id = ''
      this.editor.recipient.data.method = ''
      this.editor.recipient.active = false
    },
    emptyList() {
      return {
        name: '',
        recipients: [],
      }
    },
    setMode() {
      const name = this.$route.name.split('.')
      if (name[0] === 'reminder-list' && name[1]) {
        this.mode = name[1]
      } else {
        this.mode = 'create'
      }

      if (this.listID) {
        this.editID = this.listID
        this.mode = 'edit'
      } else if (this.$route.params.listID) {
        this.editID = this.$route.params.listID
      }

      if (this.mode === 'create') {
        this.getNewListName()
          .then(() => {
            this.list = this.emptyList()
          })
      } else if (this.mode === 'edit') {
        this.get()
      }
    },
    getNewListName() {
      return new Promise((resolve, reject) => {
        axios.get('reminder-lists/max-id')
          .then((response) => {
            let max = (!response.data) ? 1 : response.data + 1
            this.listName = 'List ' + max
            resolve (max)
          })
          .catch(error => reject(error))
      })
    },
    get() {
      axios.get('reminder-lists/' + this.editID)
        .then((response) => {
          let list = response.data
          for (let i in list.recipients) {
            list.recipients[i].phone_number.countryData = this.CountryByID(list.recipients[i].phone_number.country_id)
          }

          this.list = Object.assign({}, list)

          this.initial.name = this.list.name
        })
        .catch((error) => {
          console.log(error.response)
          this.$swal.fire({
            title: 'Reminder list not found',
            type: 'warning',
          })
        })
    },
    _reset() {
      if (this.mode === 'create') {
        this.list = Object.assign({}, this.emptyList())
      } else {
        this.get()
      }
    },
    reset() {
      this.$swal.fire({
        title: 'Reset Form',
        text: 'Are you sure?',
        type: 'question',
        confirmButtonText: 'Yes',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
      })
        .then((result) => {
          if (result.value) {
            if (this.mode === 'create') {
              this.getNewListName()
                .then(() => {
                  this.list = this.emptyList()
                })
            } else if (this.mode === 'edit') {
              this.get()
            }
          }
        })
    },
    save(emit) {
      return new Promise((resolve, reject) => {
        if (
          (this.mode === 'create' && this.listName === '') ||
          (this.mode === 'edit' && this.list.name === '')
        ) {
          this.$notify({type: 'error', title: 'List name cannot be empty'})
          reject()
          return
        }
        // create / update
        if (this.mode === 'create') {
          this.list.name = this.listName
          axios.post('reminder-lists', this.list)
            .then((response) => {
              const newList = response.data
              this.$router.push('/reminder-list/edit/' + newList.id)
              resolve(newList)
            })
            .catch((error) => reject(error))
        } else if (this.mode === 'edit') {
          axios.put('reminder-lists/' + this.list.id, this.list)
            .then(() => {
              if (this.createAlert) {
                this.$swal.fire({
                  title: 'Done',
                  text: 'Reminder list updated successfully!',
                  type: 'success',
                })
              }
              if (emit) {
                this.$emit('updated', this.list)
              }
              this.initial.name = this.list.name
              resolve()
            })
            .catch((error) => reject(error))
        }

      })
    },
  }
}
</script>
